import { memo } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatCurrency } from '../../utils/formatters';
import { budgetHealth } from './jobKpiStrip';

// Map budget-health tone → Bootstrap CSS variable so the chart Actual bar
// colours the same way as the badge on the KPI tile. Keeping the source of
// truth (threshold logic) in jobKpiStrip's `budgetHealth` helper avoids
// drift between the two visualisations.
const TONE_VAR = {
    success:   'var(--bs-success)',
    warning:   'var(--bs-warning)',
    danger:    'var(--bs-danger)',
    secondary: 'var(--bs-secondary)',
};

// Neutral Estimated-bar fill — greyed out because the Estimated total is a
// reference value, not a status. Using --bs-secondary-color so both themes
// read well (slate-400-ish in dark, slate-500-ish in light).
const ESTIMATED_FILL = 'var(--bs-secondary-color)';

// Bar chart comparing the job's Estimated vs Actual cost.
//
// Intentionally minimal in v1 — two bars, one chart. Category breakdown is
// deferred until Expense.Category is a structured field rather than free-text.
export default memo(function JobCostChart({ job }) {
    const estimatedCost = job.estimatedCost ?? 0;
    const actualCost    = job.actualCost    ?? 0;

    // Nothing to plot → render nothing. Avoids the sad empty-frame look when
    // a job has neither figure set yet.
    if (estimatedCost === 0 && actualCost === 0) return null;

    const health = budgetHealth(actualCost, estimatedCost);
    const actualFill = TONE_VAR[health.tone] ?? TONE_VAR.secondary;
    const currency = job.currency ?? 'GBP';

    const data = [
        { name: 'Estimated', value: estimatedCost, fill: ESTIMATED_FILL },
        { name: 'Actual',    value: actualCost,    fill: actualFill },
    ];

    return (
        <div className="surface h-100 recharts-outline-none">
            <h3 className="fw-semibold fs-5 mb-3">Est. vs Actual Cost</h3>
            {/* `recharts-outline-none` on the wrapper scrubs the focus
                outlines that Recharts' SVG root AND every <path> bar pick
                up when clicked. See styles.scss for the selector. */}
            <ResponsiveContainer width="100%" height={280}>
                <BarChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--bs-border-color)" />
                    <XAxis dataKey="name" stroke="var(--bs-body-color)" />
                    <YAxis tickFormatter={(v) => formatCurrency(v, currency)} stroke="var(--bs-body-color)" width={90} />
                    <Tooltip
                        formatter={(v) => [formatCurrency(v, currency), 'Cost']}
                        contentStyle={{
                            backgroundColor: 'var(--bs-body-bg)',
                            border: '1px solid var(--bs-border-color)',
                            borderRadius: '0.5rem',
                            color: 'var(--bs-body-color)',
                        }}
                        // Recharts renders the label + each item as
                        // separate nodes with their own hard-coded dark text
                        // — override both so they follow the theme instead
                        // of baking black-on-near-black in dark mode.
                        labelStyle={{ color: 'var(--bs-body-color)' }}
                        itemStyle={{ color: 'var(--bs-body-color)' }}
                        // cursor={false} disables Recharts' column-wide hover
                        // band — the bar itself is the affordance, the band
                        // is just visual noise with only two bars.
                        cursor={false}
                    />
                    <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                        {data.map((entry, i) => (
                            <Cell key={i} fill={entry.fill} />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
});
